import { PricingStrategies } from '../../../constants/enums'
import { format, differenceInDays } from 'date-fns'

// ============================================
// Plan Type Helpers
// ============================================

/**
 * Get the pricing strategy from shopPlan
 * @param {Object} shopPlan - The shop plan object
 * @returns {string} The pricing strategy
 */
export const getPlanType = (shopPlan) => {
  return shopPlan?.strategy || PricingStrategies.SERVES
}

/**
 * Check if the billing cycle is in trial period
 * @param {Object} billingCycle - The billing cycle object
 * @returns {boolean} Whether in trial period
 */
export const isTrialPeriod = (billingCycle) => {
  return billingCycle?.isTrialPeriod || false
}

/**
 * Helper object to check plan types
 */
export const isPlanType = {
  serves: (strategy) => strategy === PricingStrategies.SERVES,
  orderCount: (strategy) => strategy === PricingStrategies.ORDER_COUNT,
  flat: (strategy) => strategy === PricingStrategies.FLAT,
  revenue: (strategy) => strategy === PricingStrategies.REVENUE_ATTRIBUTION,
}

// ============================================
// Date and Period Calculations
// ============================================

/**
 * Calculate days elapsed in the billing period
 * @param {Object} billingCycle - The billing cycle object
 * @returns {number} Days elapsed
 */
export const calculateDaysElapsed = (billingCycle) => {
  if (!billingCycle?.startedOn) return 1
  
  const start = new Date(billingCycle.startedOn)
  const today = new Date()
  
  return Math.max(1, differenceInDays(today, start))
}

/**
 * Format date for display
 * @param {string} dateString - Date string in yyyy-MM-dd format
 * @returns {string} Formatted date
 */
export const formatDate = (dateString) => {
  if (!dateString) return ''
  return format(new Date(dateString), 'd MMMM, yyyy')
}

// ============================================
// Serves Plan Calculations
// ============================================

/**
 * Calculate serves usage data
 * @param {Object} billingCycle - The billing cycle object
 * @param {Object} shopPlan - The shop plan object
 * @returns {Object} Usage calculation results
 */
export const calculateServesUsage = (billingCycle, shopPlan) => {
  const currentUsage = billingCycle?.currentUsage || 0
  const maxUsage = billingCycle?.maximumRecommendationsServed || 0
  const hardLimit = billingCycle?.recommendationsServedHardLimit || 0

  const percentageUsed = hardLimit > 0 ? (currentUsage / hardLimit) * 100 : 0
  const percentageOfHardLimit = hardLimit > 0 ? (currentUsage / hardLimit) * 100 : 0

  const isOverLimit = currentUsage >= hardLimit
  const isNearLimit = percentageOfHardLimit > 80

  return {
    currentUsage,
    maxUsage,
    hardLimit,
    percentageUsed: Math.min(100, percentageUsed),
    percentageOfHardLimit: Math.min(100, percentageOfHardLimit),
    isOverLimit,
    isNearLimit,
    extraUsage: Math.max(0, currentUsage - maxUsage),
  }
}

/**
 * Calculate additional usage charge for serves plan
 * @param {Object} billingCycle - The billing cycle object
 * @param {Object} shopPlan - The shop plan object
 * @returns {number} Additional charge amount
 */
export const calculateServesAdditionalCharge = (billingCycle, shopPlan) => {
  if (!shopPlan?.isUsageBased && !shopPlan?.isExtendedUsageEnabled) return 0
  
  const extraUsage = Math.max(0, 
    (billingCycle?.currentUsage || 0) - (shopPlan?.maximumRecommendationsServed || 0)
  )
  
  if (extraUsage === 0) return 0
  
  const batchSize = shopPlan?.recommendationServesBatchSize || 1000
  const batchPrice = parseFloat(shopPlan?.recommendationServesBatchPrice || 0)
  
  return ((extraUsage / batchSize) * batchPrice).toFixed(2)
}

// ============================================
// Order Count Plan Calculations
// ============================================

/**
 * Find the current tier based on order count
 * @param {number} orderCount - Current order count
 * @param {Array} steps - Pricing tier steps
 * @returns {Object} Current tier object
 */
export const findCurrentTier = (orderCount, steps) => {
  if (!steps || steps.length === 0) return null
  
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]
    if (orderCount >= step.min && orderCount <= step.max) {
      return { ...step, index: i }
    }
  }
  
  // If over the highest tier, return the last tier
  return { ...steps[steps.length - 1], index: steps.length - 1 }
}

/**
 * Calculate order count usage and projections
 * @param {Object} billingCycle - The billing cycle object
 * @param {Object} shopPlan - The shop plan object
 * @returns {Object} Order count calculations
 */
export const calculateOrderCountUsage = (billingCycle, shopPlan) => {
  const currentOrderCount = billingCycle?.currentOrderCount || 0
  const steps = shopPlan?.steps || []
  const basePrice = parseFloat(shopPlan?.price || 0)
  
  if (steps.length === 0) {
    return {
      currentOrderCount,
      currentTier: null,
      projectedOrders: currentOrderCount,
      estimatedCharge: basePrice,
      progressPercentage: 0,
    }
  }
  
  // Find current tier
  const currentTier = findCurrentTier(currentOrderCount, steps)
  
  // Calculate velocity and projection
  const daysElapsed = calculateDaysElapsed(billingCycle)
  const daysInPeriod = 30
  const dailyVelocity = daysElapsed > 0 ? currentOrderCount / daysElapsed : 0
  const projectedOrders = Math.round(dailyVelocity * daysInPeriod)
  
  // Find projected tier
  const projectedTier = findCurrentTier(projectedOrders, steps)
  
  // Calculate progress within current tier
  const progressPercentage = currentTier ? 
    ((currentOrderCount - currentTier.min) / (currentTier.max - currentTier.min)) * 100 : 0
  
  // Calculate total charge including base price
  const stepPrice = projectedTier?.price || 0
  const totalCharge = basePrice + stepPrice
  
  return {
    currentOrderCount,
    currentTier,
    projectedOrders,
    projectedTier,
    estimatedCharge: totalCharge,
    progressPercentage: Math.min(100, Math.max(0, progressPercentage)),
    dailyVelocity: Math.round(dailyVelocity),
    daysElapsed,
    daysInPeriod,
    isOverLimit: currentTier && currentOrderCount > currentTier.max,
  }
}

/**
 * Calculate estimated charge for a given order count
 * @param {number} orderCount - Order count to estimate
 * @param {Array} steps - Pricing tier steps
 * @param {number} basePrice - Base price of the plan
 * @returns {Object} Estimation results
 */
export const calculateEstimatedCharge = (orderCount, steps, basePrice = 0) => {
  if (!steps || steps.length === 0) {
    return { estimatedCharge: parseFloat(basePrice || 0), estimatedTier: null }
  }
  
  const tier = findCurrentTier(orderCount, steps)
  const stepPrice = parseFloat(tier?.price || 0)
  const basePriceNum = parseFloat(basePrice || 0)
  const totalCharge = basePriceNum + stepPrice
  
  return {
    estimatedCharge: totalCharge,
    estimatedTier: tier,
  }
}

// ============================================
// Flat Plan Calculations
// ============================================

/**
 * Calculate flat plan charge info
 * @param {Object} shopPlan - The shop plan object
 * @returns {Object} Charge information
 */
export const calculateFlatCharge = (shopPlan) => {
  return {
    monthlyCharge: parseFloat(shopPlan?.price || 0),
    annualCharge: parseFloat(shopPlan?.annualPrice || 0),
    isAnnual: shopPlan?.onAnnual || false,
  }
}

// ============================================
// Revenue Attribution Calculations
// ============================================

/**
 * Calculate revenue attribution data
 * @param {Object} billingCycle - The billing cycle object
 * @param {Object} shopPlan - The shop plan object
 * @returns {Object} Revenue attribution calculations
 */
export const calculateRevenueAttribution = (billingCycle, shopPlan) => {
  const revenuePercent = billingCycle?.revenuePercent || 0
  const coveredRevenue = billingCycle?.coveredRevenue || 0
  const estimatedCharge = (coveredRevenue * (revenuePercent / 100)).toFixed(2)
  
  return {
    revenuePercent,
    coveredRevenue,
    estimatedCharge,
  }
}

// ============================================
// Billing Period Helpers
// ============================================

/**
 * Format billing period for display
 * @param {Object} billingCycle - The billing cycle object
 * @returns {string} Formatted billing period
 */
export const formatBillingPeriod = (billingCycle) => {
  if (!billingCycle?.startedOn || !billingCycle?.endingOn) {
    return 'Not available'
  }
  
  const start = formatDate(billingCycle.startedOn)
  const end = formatDate(billingCycle.endingOn)
  
  return `${start} - ${end}`
}

/**
 * Get remaining days in trial
 * @param {Object} billingCycle - The billing cycle object
 * @returns {number} Days remaining
 */
export const getRemainingTrialDays = (billingCycle) => {
  if (!billingCycle?.isTrialPeriod || !billingCycle?.endingOn) return 0
  
  const end = new Date(billingCycle.endingOn)
  const today = new Date()
  
  return Math.max(0, differenceInDays(end, today))
}

// ============================================
// Usage Status Helpers
// ============================================

/**
 * Determine usage status and messaging
 * @param {Object} usage - Usage calculation results
 * @returns {Object} Status information
 */
export const getUsageStatus = (usage) => {
  if (usage.isOverLimit) {
    return {
      tone: 'critical',
      message: 'Usage limit exceeded',
      showWarning: true,
    }
  }
  
  if (usage.isNearLimit) {
    return {
      tone: 'warning',
      message: 'Approaching usage limit',
      showWarning: true,
    }
  }
  
  if (usage.percentageUsed > 50) {
    return {
      tone: 'info',
      message: 'Normal usage',
      showWarning: false,
    }
  }
  
  return {
    tone: 'success',
    message: 'Low usage',
    showWarning: false,
  }
}